import * as React from 'react';

/** Custom types */
import * as JKYModel from '../custom-types';
/** Custom Hooks */
import useAuthHandler from '../utils/custom-hooks/AuthHandler';
import usePlayerHandler from '../utils/custom-hooks/PlayerHandler';
/** Utils */
import { DEFAULT_USER_AUTH, DEFAULT_PLAYER_AUTH } from '../utils/Consts';
import { getStoredUserAuth, getStoredPlayer, getStoredUser } from '../utils/Helpers';
import { setUserProperties } from '../utils/Analytics';

interface IAuthContextInterface {
  auth: JKYModel.UserAuth;
  setAuthStatus: (userAuth: JKYModel.UserAuth) => void;
  setUnauthStatus: () => void;
}

interface IPlayerContextInterface {
  player: JKYModel.PlayerAuth;
  setSelectedPlayer: (playerAuth: JKYModel.PlayerAuth) => void;
  deselectPlayer: () => void;
}

interface IGlobalContextInterface {
  currentUser: JKYModel.User;
  updateCurrentUser: (user: JKYModel.User) => void;
}

interface ICombinedContextInterface extends IGlobalContextInterface, IAuthContextInterface, IPlayerContextInterface {}

export const authContext = React.createContext<ICombinedContextInterface>({
  auth: DEFAULT_USER_AUTH,
  setAuthStatus: () => {},
  setUnauthStatus: () => {},
  player: DEFAULT_PLAYER_AUTH,
  setSelectedPlayer: () => {},
  deselectPlayer: () => {},
  currentUser: null,
  updateCurrentUser: (user: JKYModel.User) => {},
});

const { Provider } = authContext;

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { auth, setAuthStatus, setUnauthStatus } = useAuthHandler(getStoredUserAuth());
  const { player, setSelectedPlayer, deselectPlayer } = usePlayerHandler(getStoredPlayer());
  const [currentUser, setCurrentUser] = React.useState(getStoredUser());

  const updateCurrentUser = (user: JKYModel.User) => {
    window.localStorage.setItem('User', JSON.stringify(user));
    setCurrentUser(user);
  };

  // Broad GA user properties (signed in, Jookable+, two-step). Nothing that names the account.
  React.useEffect(() => {
    setUserProperties(auth && auth.token ? currentUser : null);
  }, [auth && auth.token, currentUser]);

  return (
    <Provider
      value={{
        auth,
        setAuthStatus,
        setUnauthStatus,
        player,
        setSelectedPlayer,
        deselectPlayer,
        currentUser,
        updateCurrentUser,
      }}
    >
      {children}
    </Provider>
  );
};

export default AuthProvider;
